import React, { useEffect, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { API_URL } from "helper/Constants";
import SelectDropDown from "components/ui/forms/SelectDropDown";
import { useAuth } from "provider/AuthContext";
import Auth from "service/AuthService";

const hierarchy = ["Owner", "Partner", "SuperAgent", "Agent", "User"];

const PaginatedRoleBasedDropdowns = ({
  maxSelections = 2,
  showSelectRole,
  baseRole,
  setBaseRole,
  selectedRole,
  setSelectedRole,
  selectedInputValues,
  setSelectedInputValues,
}) => {
  const { user } = useAuth();
  const authServ = new Auth(API_URL);

  const userRoleIndex = hierarchy.indexOf(user?.role);
  const filteredHierarchy = hierarchy.filter((role) => hierarchy.indexOf(role) > userRoleIndex);

  const [activeRole, setActiveRole] = useState(null);
  const [cachedUsers, setCachedUsers] = useState(null);
  const [searchInputs, setSearchInputs] = useState({});

  useEffect(() => {
    if (selectedInputValues?.prefill) {
      setActiveRole(selectedInputValues.prefill.role);
    }
  }, [selectedInputValues]);

  const handleRoleChange = (option, currentRole) => {
    setBaseRole(option.value);
    setSelectedRole((prev) => {
      const updated = { ...prev, [currentRole]: option.value };
      hierarchy.slice(hierarchy.indexOf(option.value) + 1).forEach((lower) => {
        updated[lower] = null;
      });
      return updated;
    });
    setSelectedInputValues((prev) => {
      const updated = { ...prev };
      hierarchy.slice(hierarchy.indexOf(option.value) + 1).forEach((lower) => {
        delete updated[lower];
      });
      return updated;
    });
  };

  const createLoadOptions =
    (role) =>
    async (_search, loadedOptions, { page }) => {
      const start = loadedOptions.length;
      const end = start + 10;

      if (_search.length < 3) {
        return {
          options: [],
          hasMore: false,
        };
      }

      let response;
      try {
        if (user?.role === "Owner") {
          response = await authServ.getAllUsersWithSearchText({
            searchText: _search,
            role,
            start,
            end,
          });
        } else {
          let allUsers = cachedUsers;
          if (!allUsers) {
            const allUsersResponse = await authServ.getUsersByCreatorId(user._id);
            allUsers = allUsersResponse.users?.children || [];
            setCachedUsers(allUsers);
          }
          const filteredUsers = allUsers.filter(
            (userItem) => userItem.role === role && userItem.username.toLowerCase().includes(_search.toLowerCase())
          );
          const paginatedUsers = filteredUsers.slice(start, end);
          response = {
            users: paginatedUsers,
            totalFilteredCount: filteredUsers.length,
          };
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          return {
            options: [],
            hasMore: false,
          };
        }
        return {
          options: [],
          hasMore: false,
        };
      }

      const hasMore = user?.role === "Owner" ? response.users.length === end - start : start + response.users.length < response.totalFilteredCount;
      return {
        options: response.users.map((item) => ({
          value: item._id,
          label: item.username,
        })),
        hasMore,
      };
    };

  const baseRoleIndex = hierarchy.indexOf(baseRole);
  const rolesToRender = showSelectRole
    ? filteredHierarchy.slice(0, filteredHierarchy.indexOf(baseRole) + 1)
    : baseRoleIndex !== -1
    ? hierarchy.slice(baseRoleIndex + 1)
    : [];

  const selectedCount = Object.values(selectedInputValues || {}).filter(Boolean).length;

  return (
    <div className="w-full flex flex-wrap xl:flex-nowrap gap-x-3">
      {showSelectRole && (
        <div className="relative mb-4 w-full md:w-[49%] xl:w-full">
          <SelectDropDown
            clearable
            label="Role"
            className="text-black"
            placeholder="Select option"
            selectedOption={selectedRole ?? ""}
            options={filteredHierarchy.map((role) => ({
              value: role,
              label: role === "SuperAgent" ? "Super Agent" : role,
            }))}
            handleChange={(option) => handleRoleChange(option, "role")}
          />
        </div>
      )}

      {rolesToRender.map((role) => (
        <div key={role} className="relative mb-4 w-full md:w-[49%] xl:w-full">
          <h3 className="text-black">{role}</h3>
          <AsyncPaginate
            className="text-black"
            placeholder="Type 3+ letters"
            loadOptions={createLoadOptions(role)}
            onChange={(selected) => {
              setActiveRole(role);
              setSelectedInputValues((prev) => ({ ...prev, [role]: selected }));
            }}
            onInputChange={(inputValue) => {
              setSearchInputs((prev) => ({ ...prev, [role]: inputValue }));
            }}
            additional={{ page: 0 }}
            debounceTimeout={300}
            isClearable={true}
            isDisabled={selectedCount >= maxSelections && !selectedInputValues[role]}
          />
        </div>
      ))}
    </div>
  );
};

export default PaginatedRoleBasedDropdowns;
